import React, { useState, useEffect } from 'react';
import { jobsApi, jobSchedulesApi, jobStepsApi, jobMonitorApi, publicHolidayApi } from '../services/api';
import { Job, JobSchedule, JobStep, JobStepCondition, CreateJobRequest, UpdateJobRequest, CreateJobScheduleRequest, UpdateJobScheduleRequest, CreateJobStepRequest, UpdateJobStepRequest, CreateJobStepConditionRequest, JobStepType, ConditionType, PublicHolidayJobRun, PublicHolidayJobLog, PublicHoliday, HumanFriendlySchedule, JobRunHistory, JobStepRunHistory } from '../types';
import { Plus, Edit, Trash2, Save, X, Clock, Play, RefreshCw, ChevronDown, ChevronUp, Settings, Calendar, List, CheckCircle2, XCircle } from 'lucide-react';
import ScheduleBuilder from '../components/ScheduleBuilder';
import { cronToFriendly } from '../utils/cronToFriendly';
import { cronToHumanFriendly } from '../utils/cronToHumanFriendly';
import { formatCompanyDateTime } from '../utils/companyDateFormat';
import { appConfirm } from '../context/SystemDialogContext';

const JobMonitor: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [schedules, setSchedules] = useState<JobSchedule[]>([]);
  const [steps, setSteps] = useState<JobStep[]>([]);
  const [jobRuns, setJobRuns] = useState<PublicHolidayJobRun[]>([]);
  const [jobRunHistory, setJobRunHistory] = useState<JobRunHistory[]>([]);
  const [selectedJobRunHistory, setSelectedJobRunHistory] = useState<JobRunHistory | null>(null);
  const [stepRunHistory, setStepRunHistory] = useState<JobStepRunHistory[]>([]);
  const [stepRunHistoryLoading, setStepRunHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'jobs' | 'monitor'>('jobs');
  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);
  const [expandedStepId, setExpandedStepId] = useState<number | null>(null);
  
  // Modals
  const [showJobModal, setShowJobModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showStepModal, setShowStepModal] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<JobSchedule | null>(null);
  const [editingStep, setEditingStep] = useState<JobStep | null>(null);
  
  // Forms
  const [jobForm, setJobForm] = useState<CreateJobRequest>({ name: '', description: '', isActive: true });
  const [scheduleForm, setScheduleForm] = useState<CreateJobScheduleRequest>({ isActive: true });
  const [humanSchedule, setHumanSchedule] = useState<HumanFriendlySchedule>({
    frequency: 'Daily',
    dailyInterval: 1,
    dailyTime: '02:00',
  });
  const [stepForm, setStepForm] = useState<CreateJobStepRequest>({
    stepType: 'PublicHolidayEligibility',
    isActive: true,
    conditions: [],
    timecardSyncFromWeek: null,
    timecardSyncToWeek: null,
    timecardSyncHardResyncApproved: false,
    timecardSyncHardResyncNoChanges: false
  });

  useEffect(() => {
    if (activeTab === 'jobs') {
      fetchJobs();
    } else {
      fetchJobRunHistory();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedJob) {
      fetchSchedules(selectedJob.jobId);
      fetchSteps(selectedJob.jobId);
    }
  }, [selectedJob]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const data = await jobsApi.getAll();
      setJobs(data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedules = async (jobId: number) => {
    try {
      const data = await jobSchedulesApi.getAll(jobId);
      setSchedules(data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const fetchSteps = async (jobId: number) => {
    try {
      const data = await jobStepsApi.getAll(jobId);
      setSteps(data);
    } catch (error) {
      console.error('Error fetching steps:', error);
    }
  };

  const fetchJobRuns = async () => {
    setLoading(true);
    try {
      const result = await jobMonitorApi.getJobRuns(undefined, 1, 20);
      setJobRuns(result.data);
    } catch (error) {
      console.error('Error fetching job runs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobRunHistory = async () => {
    setLoading(true);
    try {
      const result = await jobMonitorApi.getJobRunHistory(undefined, 1, 20);
      setJobRunHistory(result.data);
    } catch (error) {
      console.error('Error fetching job run history:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStepRunHistory = async (jobRunHistoryId: number) => {
    setStepRunHistoryLoading(true);
    setStepRunHistory([]);
    try {
      const data = await jobMonitorApi.getJobStepRunHistory(jobRunHistoryId);
      setStepRunHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching step run history:', error);
      setStepRunHistory([]);
    } finally {
      setStepRunHistoryLoading(false);
    }
  };

  const handleJobRunHistoryClick = async (jobRun: JobRunHistory) => {
    if (selectedJobRunHistory?.jobRunHistoryId === jobRun.jobRunHistoryId) {
      setSelectedJobRunHistory(null);
      setStepRunHistory([]);
    } else {
      setSelectedJobRunHistory(jobRun);
      if (Array.isArray(jobRun.stepRuns) && jobRun.stepRuns.length > 0) {
        setStepRunHistory(jobRun.stepRuns);
        setStepRunHistoryLoading(false);
      } else {
        await fetchStepRunHistory(jobRun.jobRunHistoryId);
      }
    }
  };

  const handleCreateJob = () => {
    setEditingJob(null);
    setJobForm({ name: '', description: '', isActive: true });
    setShowJobModal(true);
  };

  const handleEditJob = (job: Job) => {
    setEditingJob(job);
    setJobForm({ name: job.name, description: job.description || '', isActive: job.isActive });
    setShowJobModal(true);
  };

  const handleSaveJob = async () => {
    try {
      if (editingJob) {
        await jobsApi.update(editingJob.jobId, jobForm);
      } else {
        await jobsApi.create(jobForm);
      }
      setShowJobModal(false);
      fetchJobs();
    } catch (error: any) {
      alert(`Failed to save job: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteJob = async (jobId: number) => {
    if (!(await appConfirm('Are you sure you want to delete this job? This will also delete all schedules and steps.', { danger: true, confirmLabel: 'Delete' }))) {
      return;
    }
    try {
      await jobsApi.delete(jobId);
      fetchJobs();
      if (selectedJob?.jobId === jobId) {
        setSelectedJob(null);
      }
    } catch (error: any) {
      alert(`Failed to delete job: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCreateSchedule = () => {
    if (!selectedJob) return;
    setEditingSchedule(null);
    setScheduleForm({ isActive: true });
    setHumanSchedule({
      frequency: 'Daily',
      dailyInterval: 1,
      dailyTime: '02:00',
    });
    setShowScheduleModal(true);
  };

  const handleEditSchedule = (schedule: JobSchedule) => {
    setEditingSchedule(schedule);
    // Parse the cron expression back to human-friendly format
    const parsedSchedule = cronToHumanFriendly(schedule.cronExpression);
    setHumanSchedule(parsedSchedule);
    setScheduleForm({ isActive: schedule.isActive });
    setShowScheduleModal(true);
  };


  const handleSaveSchedule = async () => {
    if (!selectedJob) return;
    try {
      const request: CreateJobScheduleRequest = {
        ...scheduleForm,
        humanFriendlySchedule: humanSchedule,
      };
      
      if (editingSchedule) {
        await jobSchedulesApi.update(selectedJob.jobId, editingSchedule.scheduleId, request);
      } else {
        await jobSchedulesApi.create(selectedJob.jobId, request);
      }
      setShowScheduleModal(false);
      fetchSchedules(selectedJob.jobId);
    } catch (error: any) {
      alert(`Failed to save schedule: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteSchedule = async (scheduleId: number) => {
    if (!selectedJob) return;
    if (!(await appConfirm('Are you sure you want to delete this schedule?', { danger: true, confirmLabel: 'Delete' }))) {
      return;
    }
    try {
      await jobSchedulesApi.delete(selectedJob.jobId, scheduleId);
      fetchSchedules(selectedJob.jobId);
    } catch (error: any) {
      alert(`Failed to delete schedule: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCreateStep = () => {
    if (!selectedJob) return;
    setEditingStep(null);
    setStepForm({
      stepType: 'PublicHolidayEligibility',
      isActive: true,
      conditions: [],
      timecardSyncFromWeek: null,
      timecardSyncToWeek: null,
      timecardSyncHardResyncApproved: false,
      timecardSyncHardResyncNoChanges: false
    });
    setShowStepModal(true);
  };

  const handleEditStep = async (step: JobStep) => {
    setEditingStep(step);
    const stepData = await jobStepsApi.getById(selectedJob!.jobId, step.stepId);
    setStepForm({
      stepType: stepData.stepType,
      name: stepData.name || '',
      description: stepData.description || '',
      isActive: stepData.isActive,
      order: stepData.order,
      timecardSyncFromWeek: stepData.timecardSyncFromWeek ?? null,
      timecardSyncToWeek: stepData.timecardSyncToWeek ?? null,
      timecardSyncHardResyncApproved: stepData.timecardSyncHardResyncApproved ?? false,
      timecardSyncHardResyncNoChanges: stepData.timecardSyncHardResyncNoChanges ?? false,
      conditions: stepData.conditions?.map(c => ({
        conditionType: c.conditionType,
        value: c.value,
        description: c.description || ''
      })) || []
    });
    setShowStepModal(true);
  };

  const handleSaveStep = async () => {
    if (!selectedJob) return;
    try {
      if (editingStep) {
        await jobStepsApi.update(selectedJob.jobId, editingStep.stepId, stepForm);
      } else {
        await jobStepsApi.create(selectedJob.jobId, stepForm);
      }
      setShowStepModal(false);
      fetchSteps(selectedJob.jobId);
    } catch (error: any) {
      alert(`Failed to save step: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteStep = async (stepId: number) => {
    if (!selectedJob) return;
    if (!(await appConfirm('Are you sure you want to delete this step?', { danger: true, confirmLabel: 'Delete' }))) {
      return;
    }
    try {
      await jobStepsApi.delete(selectedJob.jobId, stepId);
      fetchSteps(selectedJob.jobId);
    } catch (error: any) {
      alert(`Failed to delete step: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleExecuteStep = async (stepId: number) => {
    if (!selectedJob) return;
    if (!(await appConfirm('Execute this step now? This will run the step immediately.', { confirmLabel: 'Execute' }))) {
      return;
    }
    try {
      const result = await jobStepsApi.execute(selectedJob.jobId, stepId);
      alert(result.message || 'Step executed successfully');
      if (activeTab === 'monitor') {
        fetchJobRunHistory();
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || error.message;
      alert(`Failed to execute step: ${errMsg}`);
      if (activeTab === 'monitor') {
        fetchJobRunHistory();
      }
    }
  };

  const addCondition = () => {
    const defaultCondition = stepForm.stepType === 'LeaveLedgerReconciliation'
      ? { conditionType: 'LeaveDateWithinDays' as ConditionType, value: -1, description: '' }
      : { conditionType: 'PublicHolidayWithinDays' as ConditionType, value: 7, description: '' };
    setStepForm({
      ...stepForm,
      conditions: [...(stepForm.conditions || []), defaultCondition]
    });
  };

  const removeCondition = (index: number) => {
    setStepForm({
      ...stepForm,
      conditions: stepForm.conditions?.filter((_, i) => i !== index) || []
    });
  };

  const updateCondition = (index: number, field: keyof CreateJobStepConditionRequest, value: any) => {
    const updated = [...(stepForm.conditions || [])];
    updated[index] = { ...updated[index], [field]: value };
    setStepForm({ ...stepForm, conditions: updated });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Job Management</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab(activeTab === 'jobs' ? 'monitor' : 'jobs')}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            {activeTab === 'jobs' ? 'View Monitor' : 'Manage Jobs'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'jobs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Jobs
          </button>
          <button
            onClick={() => setActiveTab('monitor')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'monitor'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Execution Monitor
          </button>
        </nav>
      </div>

      {activeTab === 'jobs' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Jobs List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Jobs</h2>
                <button
                  onClick={handleCreateJob}
                  className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>New</span>
                </button>
              </div>
              <div className="space-y-2">
                {jobs.map((job) => (
                  <div
                    key={job.jobId}
                    className={`p-3 rounded-lg cursor-pointer border-2 transition-colors ${
                      selectedJob?.jobId === job.jobId
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedJob(job)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold">{job.name}</h3>
                        <p className="text-sm text-gray-600">{job.description || 'No description'}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          <span>{job.scheduleCount || 0} schedules</span>
                          <span>{job.stepCount || 0} steps</span>
                          {job.isActive ? (
                            <span className="text-green-600">Active</span>
                          ) : (
                            <span className="text-gray-400">Inactive</span>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditJob(job);
                          }}
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteJob(job.jobId);
                          }}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Job Details */}
          {selectedJob && (
            <div className="lg:col-span-2 space-y-6">
              {/* Schedules */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Schedules</h2>
                  <button
                    onClick={handleCreateSchedule}
                    className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Schedule</span>
                  </button>
                </div>
                <div className="space-y-3">
                  {schedules.map((schedule) => (
                    <div key={schedule.scheduleId} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="font-semibold text-sm">{cronToFriendly(schedule.cronExpression)}</span>
                            {schedule.isActive ? (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Active</span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">Inactive</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 font-mono">Cron: {schedule.cronExpression}</p>
                          <p className="text-xs text-gray-500">Timezone: {schedule.timeZoneId || 'Company Default'}</p>
                          {schedule.hangfireJobId && (
                            <p className="text-xs text-gray-500">Hangfire ID: {schedule.hangfireJobId}</p>
                          )}
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleEditSchedule(schedule)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteSchedule(schedule.scheduleId)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {schedules.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No schedules configured</p>
                  )}
                </div>
              </div>

              {/* Steps */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Job Steps</h2>
                  <button
                    onClick={handleCreateStep}
                    className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Step</span>
                  </button>
                </div>
                <div className="space-y-3">
                  {steps.map((step) => (
                    <div key={step.stepId} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="font-semibold">Step {step.order}</span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              {step.stepType}
                            </span>
                            {step.isActive ? (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">Active</span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">Inactive</span>
                            )}
                          </div>
                          {step.name && <p className="text-sm font-medium mb-1">{step.name}</p>}
                          {step.description && <p className="text-sm text-gray-600 mb-2">{step.description}</p>}
                          {step.conditionCount && step.conditionCount > 0 && (
                            <p className="text-xs text-gray-500">{step.conditionCount} condition(s)</p>
                          )}
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleExecuteStep(step.stepId)}
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            title="Execute Step Now"
                          >
                            <Play className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditStep(step)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteStep(step.stepId)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {steps.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No steps configured</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Job Execution History</h2>
            <button
              onClick={() => fetchJobRunHistory()}
              disabled={loading}
              className="flex items-center space-x-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            </div>
          ) : jobRunHistory.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No job runs found</p>
          ) : (
            <div className="space-y-4">
              {jobRunHistory.map((run) => (
                <div key={run.jobRunHistoryId} className="border border-gray-200 rounded-lg">
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => handleJobRunHistoryClick(run)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="font-semibold">{run.jobName}</h3>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            run.triggerType === 'ManualStep' ? 'bg-purple-100 text-purple-800' :
                            run.triggerType === 'ManualJob' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {run.triggerType}
                          </span>
                          {run.triggerType === 'ManualStep' && (
                            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">Manual</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          Started: {formatCompanyDateTime(run.startedAt)}
                          {run.finishedAt && ` • Finished: ${formatCompanyDateTime(run.finishedAt)}`}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {run.stepRunCount} step(s) executed
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          run.status === 'Success' ? 'bg-green-100 text-green-800' :
                          run.status === 'Failed' ? 'bg-red-100 text-red-800' :
                          run.status === 'Partial' ? 'bg-yellow-100 text-yellow-800' :
                          run.status === 'Running' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {run.status}
                        </span>
                        {selectedJobRunHistory?.jobRunHistoryId === run.jobRunHistoryId ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    {run.errorMessage && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                        Error: {run.errorMessage}
                      </div>
                    )}
                  </div>
                  
                  {selectedJobRunHistory?.jobRunHistoryId === run.jobRunHistoryId && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      <h4 className="font-semibold mb-3 text-sm">Step Run History</h4>
                      {stepRunHistoryLoading ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Loading step runs…
                        </div>
                      ) : (() => {
                        const steps = Array.isArray(run.stepRuns) && run.stepRuns.length > 0 ? run.stepRuns : stepRunHistory;
                        return steps.length === 0 ? (
                          <p className="text-sm text-gray-500 py-2">No step runs for this job run.</p>
                        ) : (
                      <div className="space-y-2">
                        {steps.map((stepRun) => (
                          <div key={stepRun.jobStepRunHistoryId} className="bg-white rounded p-3 border border-gray-200">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="font-medium text-sm">{stepRun.stepName}</span>
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                                    {stepRun.stepType}
                                  </span>
                                  {stepRun.isManual && (
                                    <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">Manual</span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600">
                                  Started: {formatCompanyDateTime(stepRun.startedAt)}
                                  {stepRun.finishedAt && ` • Finished: ${formatCompanyDateTime(stepRun.finishedAt)}`}
                                </p>
                              </div>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                stepRun.status === 'Success' ? 'bg-green-100 text-green-800' :
                                stepRun.status === 'Failed' ? 'bg-red-100 text-red-800' :
                                stepRun.status === 'Skipped' ? 'bg-gray-100 text-gray-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {stepRun.status}
                              </span>
                            </div>
                            {stepRun.errorMessage && stepRun.status === 'Failed' && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                                <span className="font-medium">Error: </span>{stepRun.errorMessage}
                              </div>
                            )}
                            {stepRun.resultSummary && (
                              <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded text-xs text-slate-800">
                                <span className="font-medium">Result: </span>{stepRun.resultSummary}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Job Modal */}
      {showJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{editingJob ? 'Edit Job' : 'Create Job'}</h2>
              <button onClick={() => setShowJobModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={jobForm.name}
                  onChange={(e) => setJobForm({ ...jobForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={jobForm.description || ''}
                  onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={jobForm.isActive}
                  onChange={(e) => setJobForm({ ...jobForm, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm font-medium">Active</label>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowJobModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveJob}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{editingSchedule ? 'Edit Schedule' : 'Create Schedule'}</h2>
              <button onClick={() => setShowScheduleModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-6">
              <ScheduleBuilder
                schedule={humanSchedule}
                onChange={(schedule) => {
                  setHumanSchedule(schedule);
                }}
              />

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Note:</strong> Timezone is automatically set from Company Settings.
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={scheduleForm.isActive}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm font-medium">Active</label>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSchedule}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step Modal */}
      {showStepModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{editingStep ? 'Edit Step' : 'Create Step'}</h2>
              <button onClick={() => setShowStepModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Step Type *</label>
                <select
                  value={stepForm.stepType}
                  onChange={(e) => {
                    const nextType = e.target.value as JobStepType;
                    setStepForm((prev) => ({
                      ...prev,
                      stepType: nextType,
                      conditions: nextType === 'TimecardSync' ? [] : (prev.conditions || []),
                      timecardSyncFromWeek: nextType === 'TimecardSync' ? prev.timecardSyncFromWeek ?? null : null,
                      timecardSyncToWeek: nextType === 'TimecardSync' ? prev.timecardSyncToWeek ?? null : null,
                      timecardSyncHardResyncApproved: nextType === 'TimecardSync' ? prev.timecardSyncHardResyncApproved ?? false : false,
                      timecardSyncHardResyncNoChanges: nextType === 'TimecardSync' ? prev.timecardSyncHardResyncNoChanges ?? false : false
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="PublicHolidayEligibility">Public Holiday Eligibility</option>
                  <option value="PublicHolidayReconciliation">Public Holiday Reconciliation</option>
                  <option value="PublicHolidayPayrollPosting">Public Holiday Payroll Posting</option>
                  <option value="TimecardSync">Sync for Timecards</option>
                  <option value="LeaveLedgerReconciliation">Leave Ledger Reconciliation</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Order</label>
                <input
                  type="number"
                  value={stepForm.order || ''}
                  onChange={(e) => setStepForm({ ...stepForm, order: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={stepForm.name || ''}
                  onChange={(e) => setStepForm({ ...stepForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={stepForm.description || ''}
                  onChange={(e) => setStepForm({ ...stepForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={stepForm.isActive}
                  onChange={(e) => setStepForm({ ...stepForm, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm font-medium">Active</label>
              </div>
              
              {stepForm.stepType === 'TimecardSync' ? (
                <div>
                  <label className="block text-sm font-medium mb-2">Timecard Sync Filter</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">From Week Offset</label>
                      <input
                        type="number"
                        value={stepForm.timecardSyncFromWeek ?? ''}
                        onChange={(e) =>
                          setStepForm({
                            ...stepForm,
                            timecardSyncFromWeek: e.target.value === '' ? null : parseInt(e.target.value)
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">To Week Offset</label>
                      <input
                        type="number"
                        value={stepForm.timecardSyncToWeek ?? ''}
                        onChange={(e) =>
                          setStepForm({
                            ...stepForm,
                            timecardSyncToWeek: e.target.value === '' ? null : parseInt(e.target.value)
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Use week offsets relative to the current week (0 = current week, -1 = previous week, 1 = next week).
                    Leave both blank to sync only the current week.
                  </p>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="hardResyncApproved"
                        checked={stepForm.timecardSyncHardResyncApproved ?? false}
                        onChange={(e) => setStepForm({ ...stepForm, timecardSyncHardResyncApproved: e.target.checked })}
                        className="mr-2"
                      />
                      <label htmlFor="hardResyncApproved" className="text-sm">Hard Re-sync even for approved Timecard</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="hardResyncNoChanges"
                        checked={stepForm.timecardSyncHardResyncNoChanges ?? false}
                        onChange={(e) => setStepForm({ ...stepForm, timecardSyncHardResyncNoChanges: e.target.checked })}
                        className="mr-2"
                      />
                      <label htmlFor="hardResyncNoChanges" className="text-sm">Hard Re-sync even when no shift/attendance update after last sync</label>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium">Conditions</label>
                    <button
                      onClick={addCondition}
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Condition</span>
                    </button>
                  </div>
                  <div className="space-y-2">
                    {stepForm.conditions?.map((condition, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3 flex items-center space-x-2">
                        <select
                          value={condition.conditionType}
                          onChange={(e) => updateCondition(index, 'conditionType', e.target.value)}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                        >
                          {stepForm.stepType === 'LeaveLedgerReconciliation' ? (
                            <>
                              <option value="LeaveDateWithinDays">Leave Date Within Days</option>
                              <option value="LeaveDateWithinHours">Leave Date Within Hours</option>
                            </>
                          ) : (
                            <>
                              <option value="PublicHolidayWithinDays">Public Holiday Within Days</option>
                              <option value="PublicHolidayWithinHours">Public Holiday Within Hours</option>
                            </>
                          )}
                        </select>
                        <input
                          type="number"
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', parseInt(e.target.value))}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Value"
                        />
                        <input
                          type="text"
                          value={condition.description || ''}
                          onChange={(e) => updateCondition(index, 'description', e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Description (optional)"
                        />
                        <button
                          onClick={() => removeCondition(index)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {(!stepForm.conditions || stepForm.conditions.length === 0) && (
                      <p className="text-sm text-gray-500">No conditions. Step will always execute.</p>
                    )}
                    {stepForm.stepType === 'LeaveLedgerReconciliation' && (stepForm.conditions?.length ?? 0) > 0 && (
                      <p className="text-xs text-gray-500">Leave Date Within Days: -1 = yesterday only; negative (e.g. -100) = process every day from that many days ago through yesterday (backfill).</p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowStepModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveStep}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobMonitor;
